import {
  Component,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed,
  AfterViewInit,
  viewChild,
  PLATFORM_ID,
} from '@angular/core';
import * as echarts from 'echarts';
import { IEnergyTip, LISTA_AHORRO } from '../utils/lista-ahorro.data';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';
import { TableModule } from 'primeng/table';
import { CardModule } from 'primeng/card';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { PopoverModule } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { FormsModule } from '@angular/forms';
import { MessageModule } from 'primeng/message';
import { BillingService } from '../_core/billings';
import { finalize } from 'rxjs';
import { ISettlementItem, IConsumptionComparison, IDatePeriod } from '../core.interface';
import { Core } from '../_core/core';
import { DialogService } from 'primeng/dynamicdialog';
import { HomeS } from '../_core/home';
import { Spinner } from '../_core/spinner';
import { ToastService } from '../_core/toast.service';
import { RouterLink } from '@angular/router';
import { effect } from '@angular/core';
import { runInInjectionContext, Injector } from '@angular/core';
import { SurveyService } from '../_core/survey';
import { BrandingService } from '../_core/branding.service';
import { WelcomeTourSession } from '../welcome-tour/welcome-tour-session';

import { AppDownloadLinksComponent } from '../common/app-download-links/app-download-links.component';
@Component({
  selector: 'app-home',
  standalone: true,
  templateUrl: './home.html',
  providers: [DialogService],
  imports: [
    CommonModule,
    RouterLink,
    LucideAngularModule,
    TableModule,
    CardModule,
    IconFieldModule,
    InputIconModule,
    /* InputGroupAddonModule, */ InputTextModule,
    PopoverModule,
    ButtonModule,
    SkeletonModule,
    FormsModule,
    MessageModule,
  ],
  styleUrl: './home.scss',
})
export class Home implements OnInit, AfterViewInit, OnDestroy {
  private chartContainerRef = viewChild<ElementRef<HTMLElement>>('chartContainer');
  @ViewChild('carousel') carousel!: ElementRef;
  private injector = inject(Injector);
  private billingService = inject(BillingService);
  private $dialog = inject(DialogService);
  private $spinner = inject(Spinner);
  private $toast = inject(ToastService);
  private $core = inject(Core);
  private $home = inject(HomeS);
  private $branding = inject(BrandingService);
  private readonly welcomeTour = inject(WelcomeTourSession);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly surveyTenantId = signal<string | null>(null);
  private surveyStarted = false;
  private listaAhorro: IEnergyTip[] = LISTA_AHORRO;

  /** 'Android' | 'iOS' | 'Web' — según user agent (para badges de la app en home.html). */
  readonly platform: 'Android' | 'iOS' | 'Web' = this.resolvePlatform();
  readonly isMobile = this.platform === 'Android' || this.platform === 'iOS';

  public consejosRandom: IEnergyTip[] = [];

  public homeData = this.$home.homeData;
  public chartSignalLastSettlements = this.billingService.chartSettlements;

  public readonly hasBilling = computed(() => this.homeData()?.hasBilling === true);

  public readonly showUnconfiguredHome = computed(() => {
    const data = this.homeData();
    return !!data && data.hasBilling !== true && data.hasGateway !== true;
  });

  public readonly showDailyReadingsCard = computed(
    () => this.homeData()?.hasGateway === true && this.homeData()?.hasBilling !== true,
  );

  public readonly isGGM = this.$branding.isGGM;
  public readonly sendRequestsEnabled = this.$branding.sendRequestsEnabled;

  public readonly showReciboVerdeNudge = computed(
    () =>
      this.hasBilling() &&
      !!this.homeData() &&
      this.homeData()!.hasElectronicSettlement === false &&
      this.$branding.isGGM(),
  );

  public readonly hasHomeActions = computed(
    () =>
      this.hasBilling() ||
      this.showDailyReadingsCard() ||
      this.homeData()?.hasAlarmModuleAccess === true ||
      (this.sendRequestsEnabled() && !this.showReciboVerdeNudge()) ||
      this.showReciboVerdeNudge(),
  );

  public settlementsSignal = this.billingService.paginatedSettlements;
  public totalRecords = this.billingService.totalElements;
  public useFixedTableRowHeight = computed(
    () => this.settlementsSignal().length > 0 && this.settlementsSignal().length < 8,
  );
  public lastSettlement = this.billingService.lastSettlement;

  public settlements: ISettlementItem[] = [];
  public settlementsSelecteds: ISettlementItem[] = [];
  public first: number = 0;
  public rows: number = 10;
  private page: number = this.getCurrentPage();
  public searchValue: string | undefined;
  public isDownloading = signal(false);
  private initialTableLoad = true;
  public readingsReady = signal(false);
  public chartRendered = signal(false);
  public tableInitialized = signal(false);
  public readonly isNarrowViewport = signal(false);
  private narrowViewportQuery: MediaQueryList | null = null;
  private narrowViewportListener: ((event: MediaQueryListEvent) => void) | null = null;

  public showComparison = signal(false);
  public comparisonIndex = signal(0);

  public comparisonItems = computed((): IConsumptionComparison[] => {
    const data = this.homeData();
    if (!data?.hasGateway) {
      return [];
    }

    const bySupply = data.consumptionComparisonsBySupply;
    if (!bySupply?.length) {
      return [];
    }

    return bySupply.filter((item) => item.meterId == null);
  });

  public hasComparisonData = computed(() => this.comparisonItems().length > 0);

  public nextReadingDates = computed(() => this.homeData()?.nextReadingDates ?? []);

  public hasNextReadingDates = computed(() => this.nextReadingDates().length > 0);

  public activeComparison = computed((): IConsumptionComparison | null => {
    const list = this.comparisonItems();
    const index = this.comparisonIndex();
    return list[index] ?? null;
  });

  public activeSupplyTypeId = computed(() => this.activeComparison()?.supplyType?.id ?? null);
  readonly iconMap: Record<number, string> = {
    1: 'Droplets',
    2: 'Thermometer',
    3: 'Flame',
    4: 'Snowflake',
    5: 'Wind',
    6: 'Zap',
    7: 'Wind',
  };
  readonly iconMapSupplies: Record<number, string> = {
    1: 'Droplets',
    2: 'Thermometer',
    3: 'Flame',
    4: 'Snowflake',
    5: 'Wind',
    6: 'Zap',
    7: 'Wind',
  };
  readonly iconColorMap: Record<number, string> = {
    1: 'Droplets',
    2: 'Thermometer',
    3: 'Flame',
    4: 'Snowflake',
    5: 'Wind',
    6: 'Zap',
    7: 'Wind',
  };

  public tenantSelected: any;

  constructor(public surveyService: SurveyService) {
    effect(() => {
      const tenantId = this.surveyTenantId();
      if (this.surveyStarted || !tenantId || this.welcomeTour.pending()) {
        return;
      }
      this.surveyStarted = true;
      this.surveyService.startSurveyTimer(tenantId);
    });
  }

  async ngOnInit() {
    this.initNarrowViewportListener();
    this.initialTableLoad = true;
    this.first = 0;
    this.readingsReady.set(false);
    this.chartRendered.set(false);
    this.tableInitialized.set(this.isTablePrefetched());

    let tenantSelected = this.$core.selectedBuilding();
    if (!tenantSelected) {
      tenantSelected = await this.$core.getTenantFromLocal();
    }

    this.consejosRandom = this.getRandomTips();

    const prefetched = this.isDashboardPrefetched();

    if (!prefetched) {
      await this.$home.fetchHomeData();
    }

    if (!this.hasBilling() && this.hasComparisonData()) {
      this.showComparison.set(true);
    }

    this.readingsReady.set(true);

    if (this.hasBilling() && tenantSelected?.tenantId) {
      void this.billingService.prefetchHomeSettlements(tenantSelected.tenantId);
    }

    if (this.isGGM() && tenantSelected?.tenantId) {
      const tenantId = tenantSelected.tenantId;
      if (this.welcomeTour.pending()) {
        this.surveyTenantId.set(tenantId);
      } else {
        this.surveyStarted = true;
        this.surveyService.startSurveyTimer(tenantId);
      }
    }
  }

  ngOnDestroy(): void {
    if (this.narrowViewportQuery && this.narrowViewportListener) {
      this.narrowViewportQuery.removeEventListener('change', this.narrowViewportListener);
    }
  }

  get settlementsTableScrollable(): boolean {
    return !this.isNarrowViewport();
  }

  get settlementsTableScrollHeight(): string | undefined {
    if (this.isNarrowViewport() || this.useFixedTableRowHeight()) {
      return undefined;
    }
    return 'calc(-300px + 95vh)';
  }

  get settlementsTableStyle(): Record<string, string> {
    if (this.isNarrowViewport()) {
      return { width: '100%' };
    }
    if (this.useFixedTableRowHeight()) {
      return { 'min-width': '50rem' };
    }
    return { 'min-width': '50rem', 'min-height': 'calc(-400px + 95vh)' };
  }

  get settlementsTableShowCurrentPageReport(): boolean {
    return true;
  }

  get settlementsTableShowPageLinks(): boolean {
    return !this.isNarrowViewport();
  }

  get settlementsTableShowFirstLastIcon(): boolean {
    return !this.isNarrowViewport();
  }

  get settlementsTableRowsPerPageOptions(): number[] | undefined {
    return this.isNarrowViewport() ? undefined : [10, 20, 50, 100];
  }

  get settlementsTablePaginatorStyleClass(): string | undefined {
    if (!this.isNarrowViewport()) {
      return undefined;
    }
    return 'w-100 d-flex flex-wrap justify-content-center align-items-center gap-2 py-2';
  }

  ngAfterViewInit() {
    runInInjectionContext(this.injector, () => {
      effect(() => {
        const data = this.chartSignalLastSettlements();
        const container = this.chartContainerRef();
        if (
          !this.homeData() ||
          !this.hasBilling() ||
          !this.readingsReady() ||
          this.showComparison()
        ) {
          return;
        }

        if (!data.length) {
          this.chartRendered.set(true);
          return;
        }

        if (!container) return;
        this.scheduleChartInit(data);
      });
    });
  }

  private scheduleChartInit(data: ISettlementItem[]): void {
    this.chartRendered.set(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.showComparison()) return;
        const chartDom = this.chartContainerRef()?.nativeElement;
        if (!chartDom) return;
        this.initChart(data, chartDom);
      });
    });
  }

  /** KPI completo aún cargando (permisos + lecturas). */
  isKpiLoading(): boolean {
    return !this.homeData() || !this.readingsReady();
  }

  /** Gráfica ECharts pendiente de render (datos ya en signal). */
  isChartLoading(): boolean {
    if (this.showComparison()) return false;
    if (!this.hasBilling()) return false;
    if (this.isKpiLoading()) return true;
    return this.chartSignalLastSettlements().length > 0 && !this.chartRendered();
  }

  /** Action cards listas (mismos requisitos que KPI visible). */
  actionsReady(): boolean {
    return !this.isKpiLoading();
  }

  /** Tabla lazy inicial completada o precargada desde /loading. */
  tableReady(): boolean {
    return this.homeData() !== null && this.tableInitialized();
  }

  // Gates skeleton por sección: isKpiLoading (homeData + readingsReady),
  // isChartLoading (chartRendered), tableInitialized (lazy/prefetch).

  getRandomTips() {
    return [...this.listaAhorro].sort(() => Math.random() - 0.5).slice(0, 4);
  }

  private isDashboardPrefetched(): boolean {
    return this.homeData() !== null;
  }

  /** Solo reutiliza cache de página 0 (prefetch / entrada limpia). */
  private isTablePrefetched(): boolean {
    const tenantId = this.$core.selectedBuilding()?.tenantId;
    if (!tenantId) return false;
    const { from, to } = this.billingService.getDefaultSettlementsDateRange();
    return this.billingService.isReusableTableCache(tenantId, from, to);
  }

  private initChart(data: ISettlementItem[], chartDom: HTMLElement): void {
    if (chartDom.clientWidth > 0 && chartDom.clientHeight > 0) {
      this.renderChart(chartDom, data);
      return;
    }

    const ro = new ResizeObserver(() => {
      if (chartDom.clientWidth > 0 && chartDom.clientHeight > 0) {
        this.renderChart(chartDom, data);
        ro.disconnect();
      }
    });
    ro.observe(chartDom);

    window.setTimeout(() => {
      if (!this.chartRendered()) {
        this.renderChart(chartDom, data);
        ro.disconnect();
      }
    }, 300);
  }

  renderChart(dom: HTMLElement, data: ISettlementItem[]) {
    const chartData = data;
    const existingChart = echarts.getInstanceByDom(dom);
    if (existingChart) existingChart.dispose();
    const myChart = echarts.init(dom);
    const categories = chartData.map((item) => {
      const date = new Date(item.invoiceDate);
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    });
    const amounts = chartData.map((item) => item.totalAmount);
    const settlement = this.lastSettlement();
    const supplyLabel = settlement?.supplyType ?? 'suministro';
    // Rango cubierto por las barras del gráfico (no solo la última liquidación).
    const first = chartData[0];
    const last = chartData[chartData.length - 1];
    const periodLabel =
      first && last
        ? `desde el ${this.dateSpanishFormat(first.initDate)} hasta el ${this.dateSpanishFormat(last.endDate)}`
        : '';
    const option: echarts.EChartsOption = {
      title: {
        text: `Evolución de liquidaciones de ${supplyLabel}`,
        left: 'center',
        top: '0%',
        textStyle: {
          // color: 'rgb(64, 163, 64)',
          fontSize: 14,
          fontWeight: 'bold',
          fontFamily: 'SF Pro Display, sans-serif',
        },
        subtext: periodLabel || undefined,
        subtextStyle: {
          color: '#86868b',
          fontSize: 10,
        },
      },
      grid: {
        top: '15%',
        bottom: '26%',
        left: '5%',
        right: '5%',
        containLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderRadius: 12,
        borderWidth: 0,
        padding: 12,
        shadowBlur: 10,
        shadowColor: 'rgba(0,0,0,0.1)',
        textStyle: { color: '#1d1d1f', fontFamily: 'SF Pro Display, sans-serif' },
        formatter: (params: any) => {
          const p = Array.isArray(params) ? params[0] : params;
          const index = p.dataIndex;
          const dataItem = chartData[index];
          return `
            <div style="font-weight:600">${this.spanishFormat(dataItem.totalAmount)} €</div>
            <div style="font-size:11px; color:#86868b">${this.dateSpanishFormat(dataItem.initDate)} a ${this.dateSpanishFormat(dataItem.endDate)}</div>
          `;
        },
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#86868b',
          margin: 15,
        },
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: '#f5f5f7' } },
        axisLabel: {
          color: '#86868b',
          formatter: (value: number) => this.spanishFormat(value) + ' €',
        },
      },
      series: [
        {
          data: amounts,
          type: 'bar',
          barWidth: '40%',
          itemStyle: {
            color: (params: any) => {
              const index = params.dataIndex;
              const lastIndex = amounts.length - 1;
              if (index === lastIndex) {
                return 'rgba(92, 173, 92, 1)';
              }
              return 'rgb(201, 206, 201)';
            },
            borderRadius: [6, 6, 0, 0],
          },
          showBackground: true,
          backgroundStyle: {
            color: 'rgba(0, 113, 227, 0.03)',
            borderRadius: [6, 6, 0, 0],
          },
        },
      ],
    };

    myChart.setOption(option);

    window.addEventListener('resize', () => myChart.resize());
    this.chartRendered.set(true);
  }

  dateSpanishFormat(dateTime: string | Date | null | undefined): string {
    if (!dateTime) return '';
    const date = new Date(dateTime);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
  spanishFormat(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') return '0,00';
    const numberValue = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numberValue);
  }

  getLucideIcon(type: number): string {
    return this.iconMap[type] || 'HelpCircle';
  }
  getLucideIconSupplies(type: number): string {
    return this.iconMapSupplies[type] || 'HelpCircle';
  }
  getColorSupplies(supplyTypeId: number): string {
    switch (supplyTypeId) {
      case 14:
        return '#FFAB40';

      case 1:
        return '#448AFF';

      case 2:
        return '#F44336';

      case 3:
        return '#FFAB40';

      case 4:
        return '#00BCD4';

      default:
        return '#448AFF';
    }
  }

  private getCurrentPage(): number {
    return Math.floor(this.first / this.rows);
  }

  public pageChange(event: any): void {
    this.first = event.first;
    this.rows = event.rows;
    const pageToLoad = this.getCurrentPage();
    this.refresh_data(pageToLoad, event.rows, undefined, undefined, true);
  }

  async refresh_data(
    page: number,
    size: number,
    sortField?: string,
    sortOrder?: string,
    showSpinner = true,
  ): Promise<void> {
    if (!this.hasBilling()) return;

    const tenantSelected = this.$core.selectedBuilding();
    if (!tenantSelected?.tenantId) return;

    if (showSpinner) {
      this.$spinner.show();
    }

    const snapshot = this.tableDataSnapshot();
    const { from, to } = this.billingService.getDefaultSettlementsDateRange();
    await this.billingService.fetchSettlementsPaginated(
      tenantSelected.tenantId,
      from,
      to,
      page,
      size,
      sortField ?? 'invoiceDate',
      sortOrder ?? 'desc',
    );

    await this.waitForTableUpdate(snapshot);
  }

  /** Espera a que BillingService actualice paginatedSettlements (subscribe async). */
  private tableDataSnapshot(): string {
    return JSON.stringify({
      rows: this.settlementsSignal(),
      total: this.totalRecords(),
    });
  }

  private async waitForTableUpdate(beforeSnapshot: string): Promise<void> {
    const start = Date.now();
    while (Date.now() - start < 8000) {
      await new Promise((resolve) => setTimeout(resolve, 50));
      if (this.tableDataSnapshot() !== beforeSnapshot) {
        return;
      }
      if (Date.now() - start > 1500) {
        return;
      }
    }
  }

  public loadSettlementsLazy(event: any): void {
    if (!this.hasBilling()) return;

    if (this.initialTableLoad && this.isTablePrefetched()) {
      this.initialTableLoad = false;
      this.first = 0;
      this.rows = event.rows ?? this.rows;
      this.tableInitialized.set(true);
      return;
    }

    // Al entrar siempre página 1 si es la carga inicial (aunque el cache sea de otra página).
    if (this.initialTableLoad) {
      this.first = 0;
      this.rows = event.rows ?? this.rows;
    } else {
      this.first = event.first ?? 0;
      this.rows = event.rows ?? this.rows;
    }

    const pageToLoad = Math.floor(this.first / this.rows);

    // 2. Extraemos ordenación (por defecto: fecha factura descendente)
    const sortField = (event.sortField as string) ?? 'invoiceDate';
    let sortOrder = 'desc';
    if (event.sortOrder === 1) sortOrder = 'asc';
    if (event.sortOrder === -1) sortOrder = 'desc';

    // 3. Llamada a tu lógica de negocio (sin spinner en la carga inicial del lazy load)
    const showSpinner = !this.initialTableLoad;
    this.initialTableLoad = false;

    const tenantId = this.$core.selectedBuilding()?.tenantId;
    if (pageToLoad === 0 && tenantId && !this.isTablePrefetched()) {
      void this.billingService
        .prefetchHomeSettlements(tenantId)
        .finally(() => this.tableInitialized.set(true));
      return;
    }

    void this.refresh_data(pageToLoad, this.rows, sortField, sortOrder, showSpinner).finally(() =>
      this.tableInitialized.set(true),
    );
  }
  public openMobileQr(platform?: 'ios' | 'android'): void {
    if (!this.isGGM()) {
      return;
    }

    if (!this.isMobile) {
      this.$dialog.open(AppDownloadLinksComponent, {
        header: 'Descarga la App de la oficina virtual',
        closable: true,
        modal: true,
        draggable: true,
        data: platform ?? null,
      });
      return;
    }

    const androidStore = 'https://play.google.com/store/apps/details?id=com.intranetGomez';
    const iosStore = 'https://apps.apple.com/us/app/intranet-ggm/id6754910753';
    const storeUrl = this.platform === 'Android' ? androidStore : iosStore;

    if (isPlatformBrowser(this.platformId)) {
      window.location.href = storeUrl;
    }
  }
  downloadPdf(settlement: any): void {
    if (!settlement.id) return;

    const tenantSelected = this.$core.selectedBuilding();

    if (!tenantSelected?.tenantId) {
      console.error('No se encontró tenantId');
      return;
    }

    this.isDownloading.set(true);
    this.$spinner.show();
    this.billingService.downloadSettlement(tenantSelected.tenantId, settlement.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = `Liquidación_${settlement.supplyType} - ${this.dateSpanishFormat(settlement.endDate)}.pdf`;
        link.click();

        window.URL.revokeObjectURL(url);
        this.isDownloading.set(false);
        this.$spinner.hide();
      },
      error: (err) => {
        this.isDownloading.set(false);
        this.$spinner.hide();
        console.error('Error descargando PDF:', err);
        this.$toast.error('Ha ocurrido un error al descargar la liquidación.');
      },
    });
  }
  downloadSelectedSettlements(): void {
    const tenant = this.$core.selectedBuilding();

    if (!tenant?.tenantId || !this.settlementsSelecteds.length) return;

    const ids = this.settlementsSelecteds.map((s) => s.id);

    this.isDownloading.set(true);
    this.$spinner.show();
    this.billingService
      .downloadSelectedSettlements(tenant.tenantId, ids)
      .pipe(finalize(() => this.isDownloading.set(false)))
      .subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');

          link.href = url;
          link.download = `liquidaciones.zip`; // o pdf
          link.click();
          this.$spinner.hide();
          window.URL.revokeObjectURL(url);
        },
        error: (err) => {
          console.error('Error descargando lote:', err);
          this.$spinner.hide();
          this.$toast.error('Ha ocurrido un error al descargar las liquidaciones.');
        },
      });
  }

  // --- Comparison card helpers ---

  comparisonLiquidatedConsumption(): number | null {
    const raw = this.activeComparison()?.liquidatedPeriodConsumption;
    if (raw == null) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  }

  comparisonCurrentConsumption(): number | null {
    const raw = this.activeComparison()?.consumptionToDailyReadingDate;
    if (raw == null) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  }

  comparisonEstimatedConsumption(): number | null {
    const raw = this.activeComparison()?.estimatedConsumptionAtPeriodEnd;
    if (raw == null) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  }

  comparisonUnits(): string {
    return this.activeComparison()?.consumptionUnits ?? '';
  }

  comparisonPeriodBadgeLabel(): string {
    return this.hasBilling() ? 'Liquidada' : 'Mes anterior';
  }

  comparisonPreviousConsumptionLabel(): string {
    return this.hasBilling() ? 'Último consumo liquidado' : 'Mes anterior';
  }

  formatPeriodRange(period: IDatePeriod | null | undefined): string {
    if (!period?.from || !period?.to) {
      return 'Periodo no disponible';
    }
    return `${this.formatApiDate(period.from)} - ${this.formatApiDate(period.to)}`;
  }

  formatApiDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '';
    const trimmed = dateStr.trim();
    const date = /^\d{4}-\d{2}-\d{2}$/.test(trimmed)
      ? new Date(`${trimmed}T12:00:00`)
      : new Date(trimmed);
    if (Number.isNaN(date.getTime())) {
      return trimmed;
    }
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  comparisonTrendLabel(): string {
    switch (this.activeComparison()?.consumptionTrend) {
      case 'RISING':
        return 'En alza';
      case 'FALLING':
        return 'En baja';
      case 'STEADY':
        return 'Sin cambio';
      default:
        return 'Sin datos';
    }
  }

  comparisonTrendClass(): string {
    switch (this.activeComparison()?.consumptionTrend) {
      case 'RISING':
        return 'rising';
      case 'FALLING':
        return 'falling';
      case 'STEADY':
        return 'steady';
      default:
        return 'neutral';
    }
  }

  comparisonFormatReading(value: number | null): string {
    if (value == null) return '—';
    return this.spanishFormat(value);
  }

  comparisonProgressPercent(value: number | null): number {
    if (value == null || value <= 0) return 0;
    const liquidated = this.comparisonLiquidatedConsumption() ?? 0;
    const current = this.comparisonCurrentConsumption() ?? 0;
    const max = Math.max(liquidated, current);
    if (max <= 0) return 0;
    return Math.round((value / max) * 100);
  }

  goToComparison(index: number): void {
    const total = this.comparisonItems().length;
    if (index < 0 || index >= total) return;
    this.comparisonIndex.set(index);
  }

  previousComparison(): void {
    this.goToComparison(this.comparisonIndex() - 1);
  }

  nextComparison(): void {
    this.goToComparison(this.comparisonIndex() + 1);
  }

  showComparisonF() {
    if (!this.hasComparisonData()) {
      return;
    }

    const nextValue = !this.showComparison();
    this.showComparison.set(nextValue);

    if (nextValue) {
      this.comparisonIndex.set(0);
      return;
    }

    this.restoreChartAfterComparison();
  }

  private restoreChartAfterComparison(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const chartDom = this.chartContainerRef()?.nativeElement;
        if (!chartDom) {
          return;
        }

        const existingChart = echarts.getInstanceByDom(chartDom);
        if (existingChart) {
          existingChart.resize();
          this.chartRendered.set(true);
          return;
        }

        const data = this.chartSignalLastSettlements();
        if (data.length) {
          this.scheduleChartInit(data);
        } else {
          this.chartRendered.set(true);
        }
      });
    });
  }

  private formatDate(date: Date): string {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private initNarrowViewportListener(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.narrowViewportQuery = window.matchMedia('(max-width: 767px)');
    this.isNarrowViewport.set(this.narrowViewportQuery.matches);
    this.narrowViewportListener = (event: MediaQueryListEvent) => {
      this.isNarrowViewport.set(event.matches);
    };
    this.narrowViewportQuery.addEventListener('change', this.narrowViewportListener);
  }

  private resolvePlatform(): 'Android' | 'iOS' | 'Web' {
    if (!isPlatformBrowser(this.platformId)) {
      return 'Web';
    }
    const ua = navigator.userAgent;
    if (/android/i.test(ua)) {
      return 'Android';
    }
    if (/iPhone|iPad|iPod/i.test(ua)) {
      return 'iOS';
    }
    return 'Web';
  }
}
